import type { SnowplowEventSchemas } from '@gympass/event-tracker-snowplow';
import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import snakecaseKeys from 'snakecase-keys';

import { useConsent } from 'hooks/useConsent';
import { useLocalization } from 'providers/LocalizationProvider';
import { eventTracker } from 'services/eventTracker';
import { resolveCountry } from 'services/i18n';
import { formatGrantedPreferences, oneTrustClient } from 'services/oneTrust';
import { rumService } from 'services/rum';
import { isMonitoringAgentOrBot } from 'utils/browser';

const browserCountry = resolveCountry();

export type EventData = Omit<SnowplowEventSchemas['action'], 'action'> & {
  contextData?: Record<string, unknown>;
};

export type PageViewEventData = EventData & {
  redirected_on_email_validation?: boolean;
};

export type ErrorViewEventData = EventData;
export type ClickEventData = EventData;

export interface EventTrackingContext {
  userId?: string;
  record: (data: EventData) => void;
  recordPageView: (data: PageViewEventData) => void;
  recordClick: (data: ClickEventData) => void;
  recordErrorView: (data: ErrorViewEventData) => void;
}

const EventTrackingContext = createContext<EventTrackingContext | null>(null);

export const useEventTracking = () => {
  const context = useContext(EventTrackingContext);

  if (!context) {
    throw new Error(
      'useEventTracking must be used within a EventTrackingProvider',
    );
  }
  return context;
};

export function EventTrackingProvider({
  children,
  userId,
}: PropsWithChildren<{ userId?: string }>) {
  const { analytics } = useConsent();
  const { language } = useLocalization();
  const lastPreferencesRef = useRef<string | null>(null);

  const record = useCallback(
    (data: ClickEventData): void => {
      const {
        category,
        action,
        label,
        value,
        country,
        contextData = {},
      } = data;

      if (isMonitoringAgentOrBot()) {
        return;
      }

      // `Omit<SnowplowEventSchemas['action'], 'action'>` collapses every
      // field to `unknown` (a TS quirk when Omit meets the package's
      // `.passthrough()` schema type) — cast once here to the real shape.
      const eventData = {
        category,
        action,
        label,
        value,
        property: userId,
        viewer_id: userId,
        country_id: country ?? browserCountry,
        locale_id: language,
        ...snakecaseKeys(contextData),
      } as SnowplowEventSchemas['action'];

      try {
        eventTracker.record('action', eventData);
      } catch (error) {
        rumService.addError(error, { source: 'event-tracker-record' });
      }
    },
    [language, userId],
  );

  const recordErrorView = useCallback(
    (data: ErrorViewEventData) => {
      record({
        ...data,
        action: 'self_sign_up_error_view',
      });
    },
    [record],
  );

  const recordPageView = useCallback(
    (data: PageViewEventData) => {
      record({
        ...data,
        action: 'self_sign_up_page_view',
      });
    },
    [record],
  );

  const recordClick = useCallback(
    (data: ClickEventData): void => {
      record({
        ...data,
        action: 'self_sign_up_click_through',
      });
    },
    [record],
  );

  useLayoutEffect(() => {
    try {
      eventTracker.start();
    } catch (error) {
      rumService.addError(error, { source: 'event-tracker-start' });
      return;
    }

    // Separate try/catch: if start() succeeds but this throws, it's only
    // heartbeat tracking that broke, not "no events flow" — keep that
    // distinguishable in RUM rather than reporting both under one source.
    try {
      eventTracker.enableActivityTracking({
        minimumVisitLength: 30,
        heartbeatDelay: 10,
      });
    } catch (error) {
      rumService.addError(error, {
        source: 'event-tracker-enable-activity-tracking',
      });
    }
  }, []);

  useEffect(() => {
    // getAll() returns every endpoint that successfully started (openSnowcat,
    // or none if start failed).
    eventTracker.getAll().forEach(tracker => {
      try {
        if (analytics) {
          tracker.disableAnonymousTracking({
            stateStorageStrategy: 'cookieAndLocalStorage',
          });
        } else {
          tracker.enableAnonymousTracking({
            stateStorageStrategy: 'none',
            options: {
              withServerAnonymisation: true,
              withSessionTracking: true,
            },
          });
        }
      } catch (error) {
        rumService.addError(error, {
          source: 'event-tracker-consent',
          trackerId: tracker.id,
        });
      }
    });
  }, [analytics]);

  // Re-emit the consent-change event the in-house library used to send, so
  // consent-rate reporting survives the OneTrust migration. Fires only on a
  // real user consent change (not on load), and skips no-op changes. Routed
  // through `record`, which applies the bot guard and payload normalization.
  useEffect(() => {
    return oneTrustClient.onConsentChanged(() => {
      const preferences = formatGrantedPreferences(
        oneTrustClient.getSnapshot(),
      );

      if (preferences === lastPreferencesRef.current) {
        return;
      }
      lastPreferencesRef.current = preferences;

      record({
        category: 'cookies',
        action: 'cookie_preferences_opted_in',
        label: preferences,
        contextData: { preferences },
      });
    });
  }, [record]);

  const contextValue = useMemo(
    () => ({ userId, record, recordPageView, recordClick, recordErrorView }),
    [record, recordPageView, recordClick, recordErrorView, userId],
  );

  return (
    <EventTrackingContext.Provider value={contextValue}>
      {children}
    </EventTrackingContext.Provider>
  );
}
